/**
 * SiteNavbar — the site navbar and the decorations it takes from the route.
 *
 * This was `MarketingNavbar`, and the rename is the whole change: with
 * `PageShell` composing chrome from one table, ONE component renders the
 * navbar on every kind that has one, so a name that claimed one section was a
 * name that lied. Its header already said what it is — *"it IS page chrome: it
 * decides a navbar variant and a section link and renders the shell"*; that
 * argument is why `MarketingNavbar` was moved out of `features/` in the first
 * place, and it is why the listings omnibox has now followed it (the
 * `ListingsBrowseControls` beside this file).
 *
 * ## Two different questions, deliberately answered in two places
 *
 * `PageShell`'s table answers *which BANDS a page renders, and the geometry
 * they share* — navbar, the column that navbar and the page take, whether the
 * viewport is bounded, footer, mobile back band, bottom tabs, the app-banner
 * declaration. It is keyed on the manifest's `chrome:` because that is a
 * per-route POLICY decision, and it is GENERATED (webroutesgen's
 * `PageChrome::spec()`), so it is not editable from here at all. This file
 * answers *what the navbar itself shows* — a transparent variant over a hero,
 * the portal slot the home page docks its search into, a section link, the
 * browse omnibox. Folding the second question into the first would mint a
 * chrome kind per navbar decoration (a `hub` for marketing, another for browse,
 * a third for the directories) while every one of them takes the same ROW.
 *
 * ## The path predicates
 *
 * `stripLocale` + the `@letsgethome/routes` builders, not the hand regexes
 * this file used to carry (`/^(\/[a-z]{2}(-[A-Za-z]{2,4})?)?\/blog(\/|$)/`).
 * `stripLocale` matches only REGISTERED locales, so `/xy/blog` is no longer a
 * blog page, and `isUnder` is segment-anchored, so `/blogging` is not one
 * either — the same boundary bug `prefix_covers` was written for on the Rust
 * side, and the same helper `MobileBottomNav` already uses to decide its
 * active tab.
 */
import { useListingsBrowseScope } from '@/shared/components/listings/filters/use-listings-browse-scope';
import { usePathname } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { activeNavSection } from '@/shared/lib/nav-section_gen';
import { stripLocale } from '@letsgethome/lib/weburl';
import { routes } from '@letsgethome/routes';
import { useMatches } from 'react-router';

import { ListingsBrowseControls } from './ListingsBrowseControls';
import { NavbarShell, type NavbarWidth } from './NavbarShell';
import type { SectionLink } from './NavLinksClient';

/** `path` is `base` or a path SEGMENT below it — never a mere string prefix. */
function isUnder(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`);
}

export function SiteNavbar({ width }: { readonly width: NavbarWidth }) {
  const pathname = stripLocale(usePathname());
  const tBlog = useTranslations('blog');
  const tPartners = useTranslations('partners');

  // The home page is the hero page: a transparent navbar that solidifies on
  // scroll, and the portal slot `DockableHeroSearch` docks its search into.
  const isHomePage = pathname === routes.home();

  // The browse canvases — the omnibox and the filters control belong in the
  // navbar's centre slot there, which is what the deleted
  // `listings/pages/layout.tsx` hand-composed. Detail pages
  // (`/listings/{slug}`) are not browse canvases, so they keep the plain
  // navbar they have always had.
  //
  // The predicate is the ROUTE's, read through the browse-scope module: a
  // canvas is a route whose matched loader states a `browseScope`, which
  // `webroutesgen` requires of every `chrome: canvas` row. The same derivation
  // that decides the controls are SHOWN is the one that tells them what they
  // are filtering, so a canvas the navbar decorates and a canvas the control
  // understands are the same set by construction — and it covers the district
  // facets (`/districts/{slug}/{rent,buy}`), which render the same
  // `ListingsClient` and are under neither listings builder, without this file
  // or the hook knowing a single path segment.
  const browseScope = useListingsBrowseScope();
  const isListingsBrowse = browseScope !== null;
  // The primary nav's active section, DERIVED (webroutesgen's
  // `activeNavSection`): the leaf route is a browse surface ⇒ its loader's
  // `browseScope.urlMode`, anything else ⇒ none.
  const leafRouteId = useMatches().at(-1)?.id;
  const activeSection = activeNavSection(leafRouteId, browseScope);

  let sectionLink: SectionLink | undefined;
  if (isUnder(pathname, routes.blog.index())) {
    sectionLink = { label: tBlog('title'), href: routes.blog.index() };
  } else if (isUnder(pathname, routes.partners.index())) {
    sectionLink = { label: tPartners('title'), href: routes.partners.index() };
  }

  return (
    <NavbarShell
      variant={isHomePage ? 'transparent' : 'solid'}
      width={width}
      enablePortalSlot={isHomePage}
      sectionLink={sectionLink}
      activeSection={activeSection}
    >
      {isListingsBrowse ? <ListingsBrowseControls /> : undefined}
    </NavbarShell>
  );
}
